// src/pages/reports/reportList.js
import React, { useCallback, useEffect, useReducer } from "react";
import { Card, Skeleton } from "antd";
import { Bar, BarChart, Legend, Line, LineChart, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import axios from "../../services/axiosConfig";
import API_ENDPOINTS from "../../services/apiEndpoints";
import { getAuthToken } from "../../auth/auth";
import useNotification from "../../hooks/useNotification";
import Layout from "../../components/layout/layout";
import "./reportList.css";

// ─────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────
const DONATION_CATEGORIES = ["Offering", "Tithe", "Event", "Ministry", "Other"];

const EXCLUDED_ROLES = ["", "user", "no role", "-"];

// ─────────────────────────────────────────────
// Initial state
// ─────────────────────────────────────────────
const initialState = {
  userMemberData: [],
  roleData: [],
  mediaData: [],
  donationData: [],
  loadingStates: {
    media: true,
    growth: true,
    roles: true,
    donations: true
  }
};

// ─────────────────────────────────────────────
// Reducer
// ─────────────────────────────────────────────
const reportsReducer = (state, action) => {
  switch (action.type) {
    case "setUserMemberData":
      return { ...state, userMemberData: action.payload };
    case "setRoleData":
      return { ...state, roleData: action.payload };
    case "setMediaData":
      return { ...state, mediaData: action.payload };
    case "setDonationData":
      return { ...state, donationData: action.payload };
    case "setLoadingDone":
      return {
        ...state,
        loadingStates: {
          ...state.loadingStates,
          [action.payload]: false
        }
      };
    default:
      return state;
  }
};

// ─────────────────────────────────────────────
// Helper: get short month label from Date
// ─────────────────────────────────────────────
function getMonthLabel(date) {
  return date.toLocaleString("default", { month: "short" });
}

// ─────────────────────────────────────────────
// Helper: build last-6-months skeleton
// Returns array of { month, year, monthIndex } objects
// ─────────────────────────────────────────────
function buildLast6MonthsSkeleton() {
  const now = new Date();
  const months = [];

  for (let i = 5; i >= 0; i--) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push({
      month: getMonthLabel(date),
      year: date.getFullYear(),
      monthIndex: date.getMonth()
    });
  }

  return months;
}

// ─────────────────────────────────────────────
// Sub-component: ChartSkeleton
// ─────────────────────────────────────────────
function ChartSkeleton() {
  return (
    <div className="rpa-chart-skeleton">
      <Skeleton style={{height: "20em"}} active />
    </div>
  );
}

// ─────────────────────────────────────────────
// Sub-component: NoDataOverlay
// ─────────────────────────────────────────────
function NoDataOverlay({ message }) {
  return (
    <div className="rpa-no-data">
      <p className="rpa-no-data-message">{message}</p>
    </div>
  );
}

// ─────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────
function ReportsAndAnalytics() {
  const { notification, showNotification, hideNotification } = useNotification();
  const [state, dispatch] = useReducer(reportsReducer, initialState);

  const { userMemberData, roleData, mediaData, donationData, loadingStates } = state;

  // ─────────────────────────────────────────────
  // Process: Media Content Distribution
  // Builds pie data from counts of each content type
  // ─────────────────────────────────────────────
  const processMediaData = useCallback((audioCount, podcastCount, documentCount, bibleCount) => {
    return [
      { name: "Audio", value: audioCount, fill: "hsl(215, 100%, 54%)" },
      { name: "Podcasts", value: podcastCount, fill: "hsl(100, 77%, 44%)" },
      { name: "Documents", value: documentCount, fill: "hsl(35, 90%, 55%)" },
      { name: "Bible Versions", value: bibleCount, fill: "hsl(270, 60%, 55%)" }
    ];
  }, []);

  // ─────────────────────────────────────────────
  // Process: User/Member Growth (last 6 months)
  // ─────────────────────────────────────────────
  const processGrowthData = useCallback((allUsers, allMembers) => {
    const skeleton = buildLast6MonthsSkeleton();

    return skeleton.map(({ month, year, monthIndex }) => {
      // Count users created up to and including this month
      const userCount = allUsers.filter((user) => {
        if (!user.createdAt) return false;
        const d = new Date(user.createdAt);
        return d.getFullYear() < year || (d.getFullYear() === year && d.getMonth() <= monthIndex);
      }).length;

      // Count members registered up to and including this month
      const memberCount = allMembers.filter((member) => {
        if (!member.registrationDate) return false;
        const d = new Date(member.registrationDate);
        return d.getFullYear() < year || (d.getFullYear() === year && d.getMonth() <= monthIndex);
      }).length;

      return { month, users: userCount, members: memberCount };
    });
  }, []);

  // ─────────────────────────────────────────────
  // Process: Role Distribution
  // ─────────────────────────────────────────────
  const processRoleData = useCallback((allUsers) => {
    const roleCounts = {};

    allUsers.forEach((user) => {
      const role = user.role?.trim().toLowerCase() || "";

      if (EXCLUDED_ROLES.includes(role)) return;

      // Preserve original casing from first occurrence
      const displayRole = user.role.trim();
      roleCounts[displayRole] = (roleCounts[displayRole] || 0) + 1;
    });

    return Object.entries(roleCounts)
      .map(([role, count]) => ({ role, count }))
      .sort((a, b) => b.count - a.count);
  }, []);

  // ─────────────────────────────────────────────
  // Process: Donation Growth vs Previous Month
  // ─────────────────────────────────────────────
  const processDonationData = useCallback((allDonations) => {
    const now = new Date();

    const startOfCurrentMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const startOfPreviousMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const endOfPreviousMonth = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);

    return DONATION_CATEGORIES.map((category) => {
      const categoryDonations = allDonations.filter(
        // donationType is the raw backend field (confirmed from donationList.js)
        (d) => d.donationType === category
      );

      const currentTotal = categoryDonations
        .filter((d) => {
          if (!d.submittedAt) return false;
          const date = new Date(d.submittedAt);
          return date >= startOfCurrentMonth && date <= now;
        })
        .reduce((sum, d) => sum + (d.amount || 0), 0);

      const previousTotal = categoryDonations
        .filter((d) => {
          if (!d.submittedAt) return false;
          const date = new Date(d.submittedAt);
          return date >= startOfPreviousMonth && date <= endOfPreviousMonth;
        })
        .reduce((sum, d) => sum + (d.amount || 0), 0);

      return {
        category,
        amountIncrease: currentTotal - previousTotal
      };
    });
  }, []);

  // ─────────────────────────────────────────────
  // Main data fetch — runs once on mount
  // Uses Promise.allSettled so one failure does
  // not block the other charts from rendering.
  // ─────────────────────────────────────────────
  useEffect(() => {
    let isMounted = true;

    const fetchAllReportData = async () => {
      try {
        const token = await getAuthToken();

        // Donations endpoint requires fromDate + toDate.
        // We fetch start of previous month → now to cover
        // both current and previous month comparison.
        const now = new Date();
        const fromDate = new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString();
        const toDate = now.toISOString();

        const donationsUrl =
          `${API_ENDPOINTS.DONATIONS_ADMIN}`
          +`?fromDate=${encodeURIComponent(fromDate)}`
          +`&toDate=${encodeURIComponent(toDate)}`;

        const [usersResult, membersResult, donationsResult, audioResult, podcastsResult, documentsResult, bibleResult] =
          await Promise.allSettled([
            axios.get(`${API_ENDPOINTS.USERS}?status=signedup`, { headers: { Authorization: `Bearer ${token}` } }),
            axios.get(API_ENDPOINTS.MEMBERS, { headers: { Authorization: `Bearer ${token}` } }),
            axios.get(donationsUrl, { headers: { Authorization: `Bearer ${token}` } }),
            axios.get(API_ENDPOINTS.AUDIO_FILES, { headers: { Authorization: `Bearer ${token}` } }),
            axios.get(API_ENDPOINTS.PODCASTS, { headers: { Authorization: `Bearer ${token}` } }),
            axios.get(API_ENDPOINTS.DOCUMENTS, { headers: { Authorization: `Bearer ${token}` } }),
            axios.get(API_ENDPOINTS.BIBLE_VERSIONS, { headers: { Authorization: `Bearer ${token}` } })
          ]);

        if (!isMounted) return;

        const failed = [];

        // ── Users ──────────────────────────────
        if (usersResult.status === "fulfilled") {
          const allUsers = usersResult.value.data.users || [];

          // Role distribution
          const roleData = processRoleData(allUsers);
          dispatch({ type: "setRoleData", payload: roleData });

          // Growth (needs members too — handled below)
          if (membersResult.status === "fulfilled") {
            const allMembers = membersResult.value.data.users || [];

            const growthData = processGrowthData(allUsers, allMembers);
            dispatch({ type: "setUserMemberData", payload: growthData });
          } else {
            // Members failed — growth cannot be computed
            failed.push("Growth");
            console.error("Members fetch failed:", membersResult.reason);
          }
        } else {
          // Users failed — roles and growth cannot be computed
          failed.push("Role Distribution", "Growth");
          console.error("Users fetch failed:", usersResult.reason);

          // Members still needed for growth
          if (membersResult.status === "rejected") {
            failed.push("Growth");
            console.error("Members fetch failed:", membersResult.reason);
          }
        }

        // ── Donations ──────────────────────────
        if (donationsResult.status === "fulfilled") {
          const rawDonations = Array.isArray(donationsResult.value.data) ? donationsResult.value.data : [];

          const donationData = processDonationData(rawDonations);
          dispatch({ type: "setDonationData", payload: donationData });
        } else {
          failed.push("Donations");
          console.error("Donations fetch failed:", donationsResult.reason);
        }

        // ── Media Content ──────────────────────
        const audioCount = audioResult.status === "fulfilled" && Array.isArray(audioResult.value.data) ? audioResult.value.data.length : 0;

        const podcastCount =
          podcastsResult.status === "fulfilled" && Array.isArray(podcastsResult.value.data) ? podcastsResult.value.data.length : 0;

        const documentCount =
          documentsResult.status === "fulfilled" && Array.isArray(documentsResult.value.data) ? documentsResult.value.data.length : 0;

        const bibleCount =
          bibleResult.status === "fulfilled" && Array.isArray(bibleResult.value.data?.data) ? bibleResult.value.data.data.length : 0;

        const allMediaFailed =
          audioResult.status === "rejected" &&
          podcastsResult.status === "rejected" &&
          documentsResult.status === "rejected" &&
          bibleResult.status === "rejected";

        if (allMediaFailed) {
          failed.push("Media Content");
          console.error("All media fetches failed");
        } else {
          const mediaData = processMediaData(audioCount, podcastCount, documentCount, bibleCount);
          dispatch({ type: "setMediaData", payload: mediaData });
        }

        // ── Notifications ──────────────────────
        if (failed.length > 0 && failed.length < 4) {
          showNotification(`Some charts could not be loaded: ${failed.join(", ")}.`, "warning");
        } else if (failed.length === 4) {
          showNotification("Failed to load report data. Please try refreshing the page.", "error");
        }
      } catch (error) {
        // Catches getAuthToken() failure or any unexpected error
        console.error("Unexpected error in fetchAllReportData:", error);
        if (isMounted) {
          showNotification("Failed to load reports. Please try refreshing the page.", "error");
        }
      } finally {
        if (isMounted) {
          // Mark all charts as done loading regardless of outcome
          ["growth", "roles", "media", "donations"].forEach((key) => {
            dispatch({ type: "setLoadingDone", payload: key });
          });
        }
      }
    };

    fetchAllReportData();

    return () => {
      isMounted = false;
    };
  }, [processGrowthData, processRoleData, processMediaData, processDonationData, showNotification]);

  // ─────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────
  return (
    <Layout
      navTitle="Reports & Analytics"
      notificationProps={[
        {
          show: notification.show,
          message: notification.message,
          type: notification.type,
          onClose: hideNotification,
          duration: 4000
        }
      ]}
    >
      <div className="rpa-page-wrapper">
        <div className="rpa-row">
          {/* ── Chart 1: User/Member Growth ── */}
          <div className="rpa-col-left">
            <Card className="rpa-card" title="Growth: Users, Members">
              {loadingStates.growth ? (
                <ChartSkeleton />
              ) : userMemberData.length === 0 ? (
                <NoDataOverlay message="Growth data could not be loaded." />
              ) : (
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={userMemberData}>
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="users" stroke="hsl(100, 77%, 44%)" strokeWidth={3} dot={{ r: 4 }} name="No. of Users" />
                    <Line
                      type="monotone"
                      dataKey="members"
                      stroke="hsl(215, 100%, 54%)"
                      strokeWidth={3}
                      dot={{ r: 4 }}
                      name="No. of Members"
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </Card>
          </div>

          {/* ── Chart 2: Role Distribution ── */}
          <div className="rpa-col-right">
            <Card className="rpa-card" title="Roles: Assigned Count">
              {loadingStates.roles ? (
                <ChartSkeleton />
              ) : roleData.length === 0 ? (
                <NoDataOverlay message="No roles have been assigned yet." />
              ) : (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={roleData} layout="vertical">
                    <XAxis type="number" />
                    <YAxis dataKey="role" type="category" width={150} />
                    <Tooltip />
                    <Bar dataKey="count" name="Head Count" fill="hsl(215, 100%, 54%)" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Card>
          </div>
        </div>

        <div className="rpa-row">
          {/* ── Chart 3: Media Content Distribution ── */}
          <div className="rpa-col-left">
            <Card className="rpa-card" title="Media Content: Upload Count">
              {loadingStates.media ? (
                <ChartSkeleton />
              ) : mediaData.length === 0 ? (
                <NoDataOverlay message="Media content data could not be loaded." />
              ) : (
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={mediaData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, value }) => `${name}: ${value}`}
                      outerRadius={80}
                      dataKey="value"
                    />
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </Card>
          </div>

          {/* ── Chart 4: Donation Growth ── */}
          <div className="rpa-col-right">
            <Card className="rpa-card" title="Donations: Increase from previous month">
              {loadingStates.donations ? (
                <ChartSkeleton />
              ) : donationData.length === 0 ? (
                <NoDataOverlay message="Donation data could not be loaded." />
              ) : (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={donationData}>
                    <XAxis dataKey="category" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="amountIncrease" name="Increased by" fill="hsl(215, 100%, 54%)" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default ReportsAndAnalytics;
